import React, { useState, useEffect, useRef, useCallback, ChangeEvent, KeyboardEvent } from "react";
import { useWebSocketContext } from "../../contexts/WebSocketContext";
import { WebSocketMessage } from "../../types/websocket";
import { SessionParticipant, sessionService } from "../../services/sessionService";
import "./ChatInterface.css";

// Type guard helper for message data
const hasProperty = (obj: unknown, prop: string): obj is Record<string, unknown> => {
  return typeof obj === "object" && obj !== null && prop in obj;
};

const getMessageDataProperty = (data: unknown, prop: string): string => {
  if (hasProperty(data, prop) && typeof data[prop] === "string") {
    return data[prop] as string;
  }
  return "";
};

interface ChatInterfaceProps {
  className?: string;
  maxHeight?: string;
  showUserJoinLeave?: boolean;
  sessionId?: string;
  participants?: SessionParticipant[];
  currentUserId?: string;
}

type ChatMode = "broadcast" | "whisper";

const messageTypeColors: Record<string, string> = {
  ic: "text-blue-300",
  ooc: "text-green-300",
  whisper: "text-purple-300",
  general: "text-gray-100",
};

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  className = "",
  maxHeight = "400px",
  showUserJoinLeave = true,
  sessionId,
  participants = [],
  currentUserId,
}) => {
  const {
    isConnected,
    getChatMessages,
    getSystemNotifications,
    getDiceRolls,
    sendChatMessage,
    getUserUsername,
    refreshUserCache,
    prependMessages,
  } = useWebSocketContext();

  const [message, setMessage] = useState("");
  const [mode, setMode] = useState<ChatMode>("broadcast");
  const [isInCharacter, setIsInCharacter] = useState(true);
  const [whisperTarget, setWhisperTarget] = useState<string>("");
  const [historyLoaded, setHistoryLoaded] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const chatMessages = getChatMessages(sessionId);
  const systemMessages = showUserJoinLeave ? getSystemNotifications(sessionId) : [];
  const diceRollMessages = getDiceRolls(sessionId);

  const allMessages = [...chatMessages, ...systemMessages, ...diceRollMessages].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  // Load chat history from the REST API once when connected to a session
  const loadChatHistory = useCallback(async () => {
    if (!sessionId || historyLoaded === sessionId) return;
    try {
      const resp = await sessionService.getChatHistory(sessionId, 50);
      if (resp.messages && resp.messages.length > 0) {
        // Convert persisted messages to WebSocketMessage shape so they fit the message store
        const historical: WebSocketMessage[] = resp.messages.map((m) => ({
          id: `history-${m.id}`,
          type:
            m.message_type === "ic"
              ? "chat_ic"
              : m.message_type === "ooc"
                ? "chat_ooc"
                : m.message_type === "dice_roll"
                  ? "dice_roll"
                  : "chat_system",
          sessionId: String(m.session_id),
          senderId: String(m.user_id),
          timestamp: m.sent_at,
          data:
            m.message_type === "dice_roll"
              ? {
                  formula: m.content,
                  characterName: m.character_name,
                  senderUsername: m.sender_username,
                  isHistorical: true,
                }
              : {
                  message: m.content,
                  characterName: m.character_name,
                  senderUsername: m.sender_username,
                  messageType: m.message_type,
                  isHistorical: true,
                },
        }));
        prependMessages(historical);
      }
      setHistoryLoaded(sessionId);
    } catch (err) {
      console.error("Failed to load chat history:", err);
    }
  }, [sessionId, historyLoaded, prependMessages]);

  // Load history when first connected
  useEffect(() => {
    if (isConnected && sessionId) {
      loadChatHistory();
    }
  }, [isConnected, sessionId, loadChatHistory]);

  // Reset history marker when session changes
  useEffect(() => {
    setHistoryLoaded(null);
  }, [sessionId]);

  // Extract unique user IDs and refresh cache
  useEffect(() => {
    const userIds = new Set<string>();
    allMessages.forEach((msg) => {
      if (msg.senderId) userIds.add(msg.senderId);
    });
    if (userIds.size > 0) refreshUserCache(Array.from(userIds));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allMessages.length, refreshUserCache]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [allMessages.length]);

  const handleSendMessage = () => {
    if (!message.trim() || !isConnected) return;

    let messageType: string;
    let recipientId: string | undefined;

    if (mode === "broadcast") {
      messageType = isInCharacter ? "IC" : "OOC";
    } else {
      if (!whisperTarget) return;
      messageType = "whisper";
      recipientId = whisperTarget;
    }

    const success = sendChatMessage(message, messageType, undefined, recipientId, sessionId);
    if (success) setMessage("");
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setMessage(e.target.value);
  };

  const formatMessageTime = (timestamp: string): string =>
    new Date(timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const getMessageTypeFromData = (msg: WebSocketMessage): string => {
    switch (msg.type) {
      case "chat_ic":
        return "ic";
      case "chat_ooc":
        return "ooc";
      case "chat_whisper":
        return "whisper";
      case "user_joined":
      case "user_left":
      case "system_alert":
        return "system";
      default:
        return "general";
    }
  };

  const otherParticipants = participants.filter((p) => String(p.user_id) !== String(currentUserId));

  // ── Message rendering ──────────────────────────────────────────────────────

  const renderMessage = (msg: WebSocketMessage, index: number) => {
    const messageType = getMessageTypeFromData(msg);
    const isSystemMessage = ["user_joined", "user_left", "system_alert", "chat_system"].includes(
      msg.type
    );
    const time = formatMessageTime(msg.timestamp);

    if (isSystemMessage) {
      const content =
        getMessageDataProperty(msg.data, "message") ||
        getMessageDataProperty(msg.data, "text") ||
        "System notification";
      return (
        <div key={`${msg.id}-${index}`} className="chat-message system-message">
          <span className="text-yellow-400 text-sm italic">
            {time} • {content}
          </span>
        </div>
      );
    }

    if (msg.type === "dice_roll") {
      const formula = getMessageDataProperty(msg.data, "formula") || "";
      const characterName = getMessageDataProperty(msg.data, "characterName");
      const rollName = getMessageDataProperty(msg.data, "rollName");
      const isPrivate =
        hasProperty(msg.data, "isPrivate") &&
        (msg.data as Record<string, unknown>)["isPrivate"] === true;
      const totalVal =
        hasProperty(msg.data, "total") &&
        typeof (msg.data as Record<string, unknown>)["total"] === "number"
          ? ((msg.data as Record<string, unknown>)["total"] as number)
          : 0;
      const resultsVal =
        hasProperty(msg.data, "results") &&
        Array.isArray((msg.data as Record<string, unknown>)["results"])
          ? ((msg.data as Record<string, unknown>)["results"] as number[])
          : [];
      const isCritical =
        hasProperty(msg.data, "isCritical") &&
        (msg.data as Record<string, unknown>)["isCritical"] === true;
      // Historical rolls carry only a pre-formatted summary string in `formula`
      // (e.g. "Attack: 1d20+5 → 18"); they have no numeric total to append.
      const isHistorical =
        hasProperty(msg.data, "isHistorical") &&
        (msg.data as Record<string, unknown>)["isHistorical"] === true;
      const rollerName = characterName || getUserUsername(msg.senderId) || msg.senderId;
      const totalColor = isCritical
        ? "text-green-400 font-bold"
        : totalVal === 1 && formula.toLowerCase().includes("d20")
          ? "text-red-400 font-bold"
          : "text-blue-300 font-semibold";

      return (
        <div
          key={`${msg.id}-${index}`}
          className="chat-message dice-roll-message border-l-4 border-indigo-500 pl-2 py-1 my-0.5 bg-indigo-950/30 rounded-r"
        >
          <div className="flex items-start space-x-2">
            <span className="text-gray-400 text-xs min-w-[40px]">{time}</span>
            <div className="flex-1 text-sm">
              <span className="text-indigo-300 font-semibold">🎲 {rollerName}</span>
              {rollName && <span className="text-yellow-300 ml-1">({rollName})</span>}
              {isPrivate && <span className="text-purple-400 text-xs ml-1">[Private]</span>}
              <span className="text-gray-400 mx-1">rolled</span>
              <span className="font-mono text-gray-300">{formula}</span>
              {!isHistorical && (
                <>
                  <span className="text-gray-500 mx-1">→</span>
                  <span className={`text-lg ${totalColor}`}>{totalVal}</span>
                  {resultsVal.length > 0 && (
                    <span className="text-gray-500 text-xs ml-1">[{resultsVal.join(", ")}]</span>
                  )}
                  {isCritical && <span className="text-green-400 text-xs ml-1">✨ Crit!</span>}
                </>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Regular chat message
    const senderName = (() => {
      const charName = getMessageDataProperty(msg.data, "characterName");
      if (charName) return charName;
      const sName = getMessageDataProperty(msg.data, "senderName");
      if (sName) return sName;
      const username = getUserUsername(msg.senderId);
      if (username) return username;
      return msg.senderId;
    })();
    const messageText =
      getMessageDataProperty(msg.data, "message") || getMessageDataProperty(msg.data, "text");
    const colorClass = messageTypeColors[messageType] || "text-gray-100";

    return (
      <div key={`${msg.id}-${index}`} className={`chat-message ${messageType}-message`}>
        <div className="flex items-start space-x-2">
          <span className="text-gray-400 text-xs min-w-[40px]">{time}</span>
          <div className="flex-1">
            <span className={`font-semibold ${colorClass}`}>
              {senderName}
              {messageType === "whisper" && getMessageDataProperty(msg.data, "recipientName") && (
                <span className="text-purple-400">
                  {" → "}
                  {getMessageDataProperty(msg.data, "recipientName")}
                </span>
              )}
              {messageType === "ooc" && <span className="text-green-500 text-xs ml-1">[OOC]</span>}:
            </span>
            <span className={`ml-2 ${colorClass}`}>{messageText}</span>
          </div>
        </div>
      </div>
    );
  };

  // ── Send button disabled state ──────────────────────────────────────────────
  const sendDisabled = !isConnected || !message.trim() || (mode === "whisper" && !whisperTarget);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div
      className={`chat-interface flex flex-col bg-zinc-900 ${className}`}
      data-testid="chat-interface"
    >
      {/* Messages area */}
      <div className="chat-messages flex-1 p-3 overflow-y-auto codex-scroll" style={{ maxHeight }}>
        {allMessages.length === 0 ? (
          <div className="text-zinc-500 text-sm italic text-center py-4">
            No messages yet. Start the conversation!
          </div>
        ) : (
          allMessages.map((msg, index) => renderMessage(msg, index))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="chat-input flex-shrink-0 bg-zinc-800 p-3 border-t border-zinc-700">
        {/* Mode selector */}
        <div className="flex items-center space-x-1 mb-2">
          <button
            onClick={() => setMode("broadcast")}
            className={`flex-1 py-1 text-xs rounded transition-colors font-medium ${
              mode === "broadcast"
                ? "bg-amber-600 text-white"
                : "bg-zinc-700 text-zinc-400 hover:bg-zinc-600"
            }`}
          >
            📢 Broadcast
          </button>
          <button
            onClick={() => setMode("whisper")}
            className={`flex-1 py-1 text-xs rounded transition-colors font-medium ${
              mode === "whisper"
                ? "bg-purple-700 text-white"
                : "bg-zinc-700 text-zinc-400 hover:bg-zinc-600"
            }`}
          >
            🤫 Whisper
          </button>
        </div>

        {/* Broadcast: IC/OOC toggle */}
        {mode === "broadcast" && (
          <div className="flex items-center space-x-2 mb-2">
            <span className="text-xs text-zinc-400 select-none">In Character</span>
            <button
              role="switch"
              aria-checked={isInCharacter}
              onClick={() => setIsInCharacter((p) => !p)}
              title={isInCharacter ? "Switch to Out of Character" : "Switch to In Character"}
              className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 ${
                isInCharacter ? "bg-blue-600" : "bg-zinc-600"
              }`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                  isInCharacter ? "translate-x-[18px]" : "translate-x-[3px]"
                }`}
              />
            </button>
            <span
              className={`text-xs font-medium select-none ${
                isInCharacter ? "text-blue-400" : "text-green-400"
              }`}
            >
              {isInCharacter ? "IC" : "OOC"}
            </span>
          </div>
        )}

        {/* Whisper: target dropdown */}
        {mode === "whisper" && (
          <select
            value={whisperTarget}
            onChange={(e) => setWhisperTarget(e.target.value)}
            className="w-full px-3 py-1 mb-2 bg-zinc-700 text-white rounded text-sm border border-zinc-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">Whisper to…</option>
            {otherParticipants.map((p) => (
              <option key={p.user_id} value={String(p.user_id)}>
                {p.character_name || p.username}
              </option>
            ))}
          </select>
        )}

        {/* Message input + send */}
        <div className="flex space-x-2">
          <input
            ref={inputRef}
            type="text"
            value={message}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder={
              mode === "broadcast"
                ? isInCharacter
                  ? "Say something in character…"
                  : "Say something out of character…"
                : "Whisper a message…"
            }
            className="flex-1 px-3 py-2 bg-zinc-700 text-white placeholder-zinc-400 rounded text-sm border border-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500"
            disabled={!isConnected}
          />
          <button
            onClick={handleSendMessage}
            disabled={sendDisabled}
            className="px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:bg-zinc-600 disabled:cursor-not-allowed transition-colors text-sm font-medium"
          >
            Send
          </button>
        </div>

        {!isConnected && <div className="text-red-400 text-xs mt-2">Disconnected from server</div>}
      </div>
    </div>
  );
};
